import React, { useCallback, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader } from 'fronton-react';
import { ChooseKeycard } from './choose-keycard/choose-keycard';
import { SuccessRegistration } from './success-registration/success-registration';
import { FakeModalFooter } from './styles';
import { GenerateCode } from './generate-code/generate-code';
import { LoadingDimmer } from './loading-dimmer/loading-dimmer';
import { VerifyCode } from './verify-code/verify-code';
import { useWindowSize } from 'fronton-react/hooks/use-window-size';
import { useStoreon } from 'storeon/react';
import { ERegistrationStep } from 'reducers/loyalty-registration-modal-reducer';
import { State } from '../../store/types';
import {
  CHECK_PHONE_VERIFY_REQUEST,
  GENERATE_CODE_REQUEST,
  REGISTRATION_MODAL,
  VERIFY_CODE_REQUEST,
} from 'actions/loyalty-registration-modal';
import { useTranslation } from 'react-i18next';
import { useLoyaltyRegistrationModalEvents } from './hooks/use-loyalty-registration-modal-events';

export const LoyaltyRegistrationModal: React.FC = () => {
  const { innerWidth } = useWindowSize();
  const { t } = useTranslation();
  const {
    loyaltyRegistrationModal: {
      step,
      show,
      checkPhoneVerifyRequest,
      verifyCodeRequest,
      codeVerifyStep,
      generateCodeRequest,
      successStep,
      createLoyaltyRequest,
    },
    dispatch,
  } = useStoreon<State>('loyaltyRegistrationModal');

  useLoyaltyRegistrationModalEvents();

  const sendCode = useCallback(
    (phoneNumber) => {
      dispatch(GENERATE_CODE_REQUEST.request, { phoneNumber });
    },
    [dispatch],
  );

  const verifyCode = useCallback(
    (phoneNumber, code) => {
      dispatch(VERIFY_CODE_REQUEST.request, { phoneNumber, code });
    },
    [dispatch],
  );

  const stepContent = useMemo(() => {
    switch (step) {
      case ERegistrationStep.CARD_CHOOSE:
        return (
          <ChooseKeycard
            fetchPhoneVerify={() => dispatch(CHECK_PHONE_VERIFY_REQUEST.request)}
            setKeycardTypeAndMarketingConsent={(keycardType, marketingConsent) =>
              dispatch(REGISTRATION_MODAL.setChooseKeycardData, { keycardType, marketingConsent })
            }
          />
        );
      case ERegistrationStep.CODE_GENERATE:
        return (
          <GenerateCode
            sendCode={sendCode}
            loading={generateCodeRequest.loading}
            error={generateCodeRequest.error}
            forcedPhoneNumber={codeVerifyStep.forcedPhoneNumber}
          />
        );
      case ERegistrationStep.CODE_SEND:
        return (
          <VerifyCode
            phoneNumber={codeVerifyStep.phoneNumber}
            sendCode={sendCode}
            loading={verifyCodeRequest.loading}
            error={verifyCodeRequest.error}
            verifyCode={verifyCode}
          />
        );
      case ERegistrationStep.SUCCESS:
        if (successStep.keycardType && successStep.cardNumber) {
          return (
            <SuccessRegistration
              keycardType={successStep.keycardType}
              cardNumber={successStep.cardNumber}
              onClose={() => dispatch(REGISTRATION_MODAL.close)}
            />
          );
        }

        return <></>;
      default:
        return <></>;
    }
  }, [
    step,
    successStep,
    codeVerifyStep,
    generateCodeRequest,
    verifyCodeRequest,
    dispatch,
    sendCode,
    verifyCode,
  ]);

  const loading = checkPhoneVerifyRequest.loading || createLoyaltyRequest.loading;

  return (
    <>
      <Modal
        variant={innerWidth <= 440 ? 'full-screen' : 'curtain'}
        show={show}
        size="m"
        onClose={() => dispatch(REGISTRATION_MODAL.close)}
      >
        <LoadingDimmer active={loading}>
          <ModalHeader title={t(`loyalty-registration-modal.modalTitle.${step}`)} />
          <ModalContent data-qa="loyalty-registration-modal">{stepContent}</ModalContent>
          <FakeModalFooter />
        </LoadingDimmer>
      </Modal>
    </>
  );
};
